import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Explore as ExploreIcon,
  Route as FlowIcon,
  Save as SaveIcon,
  Language as PageIcon,
} from '@mui/icons-material';
import { NLExplorationResult as NLExplorationResultType, NLDiscoveredFlow } from '../types';
import NLTestStepsViewer from './NLTestStepsViewer';

interface NLExplorationResultProps {
  result: NLExplorationResultType;
  onSaveFlow?: (flow: NLDiscoveredFlow) => void;
}

const NLExplorationResult: React.FC<NLExplorationResultProps> = ({ result, onSaveFlow }) => {
  const [expandedFlow, setExpandedFlow] = useState<string | false>(false);

  return (
    <Box sx={{ mb: 1.5 }}>
      {/* Summary header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <ExploreIcon color="primary" />
        <Typography variant="subtitle2" fontWeight={700}>
          App Exploration Results
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Explored {result.pages_discovered} pages, discovered {result.flows.length} user flows
      </Typography>

      {/* Metric cards */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Paper variant="outlined" sx={{ px: 1.5, py: 0.75, minWidth: 80, textAlign: 'center' }}>
          <PageIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" fontWeight={700}>{result.pages_discovered}</Typography>
          <Typography variant="caption" color="text.secondary">Pages</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ px: 1.5, py: 0.75, minWidth: 80, textAlign: 'center' }}>
          <Typography variant="subtitle2" fontWeight={700}>{result.transitions_recorded}</Typography>
          <Typography variant="caption" color="text.secondary">Transitions</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ px: 1.5, py: 0.75, minWidth: 80, textAlign: 'center' }}>
          <FlowIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" fontWeight={700}>{result.flows.length}</Typography>
          <Typography variant="caption" color="text.secondary">Flows</Typography>
        </Paper>
      </Box>

      {/* Pages table */}
      {result.pages.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Discovered Pages
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>URL</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }} align="center">Links</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }} align="center">Depth</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.pages.map((page, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {page.url}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {page.title || '-'}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{page.links_count}</TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{page.depth}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Discovered flows */}
      {result.flows.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Discovered Flows
          </Typography>
          {result.flows.map((flow, idx) => (
            <Accordion
              key={idx}
              expanded={expandedFlow === flow.name}
              onChange={(_, expanded) => setExpandedFlow(expanded ? flow.name : false)}
              variant="outlined"
              sx={{ mb: 0.5 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <FlowIcon fontSize="small" color="primary" />
                  <Typography variant="body2" fontWeight={600}>
                    {flow.name}
                  </Typography>
                  {flow.flow_type && (
                    <Chip size="small" label={flow.flow_type} variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                  )}
                  <Chip size="small" label={`${flow.steps.length} steps`} sx={{ height: 20, fontSize: '0.7rem' }} />
                  <Box sx={{ flexGrow: 1 }} />
                  {onSaveFlow && (
                    <Button
                      size="small"
                      startIcon={<SaveIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSaveFlow(flow);
                      }}
                      sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                    >
                      Save as Test
                    </Button>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {flow.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {flow.description}
                  </Typography>
                )}
                <Chip
                  size="small"
                  label={flow.entry_url}
                  variant="outlined"
                  sx={{ fontFamily: 'monospace', fontSize: '0.75rem', mb: 1 }}
                />
                <NLTestStepsViewer
                  plan={{
                    description: flow.name,
                    base_url: flow.entry_url,
                    steps: flow.steps,
                    step_count: flow.steps.length,
                  }}
                  compact
                />
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Mermaid graph source */}
      {result.mermaid_graph && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Flow Graph
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              bgcolor: 'grey.50',
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              whiteSpace: 'pre-wrap',
              overflow: 'auto',
              maxHeight: 200,
            }}
          >
            {result.mermaid_graph}
          </Paper>
        </Box>
      )}

      {/* Start URL */}
      <Typography variant="caption" color="text.secondary">
        Started from: {result.start_url}
      </Typography>
    </Box>
  );
};

export default NLExplorationResult;
